import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../../BaseController';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ActionAPI, FormAPI, InputAPI, ModalProps, ModalService, RadioGroupAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { FragmentNames, ScoreNasViewerAliases } from '../../../../codegen/index';
import { radioExpressionData } from '../radio-group-data/expression-yes-no';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../../constants/routes';
import { RadioGroupData, ScoreNASResponse } from '../../score-types';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { ScoreAndIndexesService, ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { RadioGroupExpressions } from '../radio-group-data/radio-group-expressions';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { HealthProfessionalUser } from '../../../../app/authentication';

@Controller({ fragment: FragmentNames.SCORE_NAS_VIEWER })
export default class ScoreNasViewerController extends BaseController {

  @Inject(ScoreNasViewerAliases.FORM_SCORE_NAS_VIEWER)
  private formAPI!: FormAPI;

  @Inject(ScoreNasViewerAliases.INPUT_EVALUATION_DATE)
  private inputDateAPI!: InputAPI;

  @Inject(ScoreNasViewerAliases.FIELD_HOUR)
  private inputHourAPI!: InputAPI;

  @Inject(ScoreNasViewerAliases.FIELD_PROFESSIONAL)
  private inputProfessional!: InputAPI;

  @Inject(ScoreNasViewerAliases.ACTION_NEW_SCORE_NAS)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreNasViewerAliases.ACTION_REFERENCE_SCORE_NAS)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScoreNasViewerAliases.ACTION_INACTIVATE_SCORE_NAS)
  private actionInactivateAPI!: ActionAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_MONITORING_CONTROL_A)
  private radioGroupMonitoringControlA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_MONITORING_CONTROL_B)
  private radioGroupMonitoringControlB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_MONITORING_CONTROL_C)
  private radioGroupMonitoringControlC!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_MOBILITY_A)
  private radioGroupMobilityA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_MOBILITY_B)
  private radioGroupMobilityB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_MOBILITY_C)
  private radioGroupMobilityC!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_ADMIN_TASKS_A)
  private radioGroupAdminTasksA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_ADMIN_TASKS_B)
  private radioGroupAdminTasksB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_ADMIN_TASKS_C)
  private radioGroupAdminTasksC!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_HYGIENE_A)
  private radioGroupHygieneA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_HYGIENE_B)
  private radioGroupHygieneB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_HYGIENE_C)
  private radioGroupHygieneC!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_DRAIN)
  private radioGroupDrain!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_SUPPORT_CARDIO_A)
  private radioGroupSupportCardioA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_CARDIO_B)
  private radioGroupSupportCardioB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_CARDIO_C)
  private radioGroupSupportCardioC!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_CARDIO_D)
  private radioGroupSupportCardioD!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_A)
  private radioGroupSupportA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_B)
  private radioGroupSupportB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_LABORATORY_INVESTIGATION_A)
  private radioGroupLaboratory!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_MEDICATION)
  private radioGroupMedication!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_VENTILATORY_A)
  private radioGroupSupportVentilatoryA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_VENTILATORY_B)
  private radioGroupSupportVentilatoryB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_SUPPORT_VENTILATORY_C)
  private radioGroupSupportVentilatoryC!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_RENAL_SUPPORT_A)
  private radioGroupRenalSupportA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_RENAL_SUPPORT_B)
  private radioGroupRenalSupportB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_NEUROLOGIC_SUPPORT)
  private radioGroupNeurologicSupport!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_METABOLIC_SUPPORT_A)
  private radioGroupMetabolicSupportA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_METABOLIC_SUPPORT_B)
  private radioGroupMetabolicSupportB!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_METABOLIC_SUPPORT_C)
  private radioGroupMetabolicSupportC!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_SPECIAL_INTERVETIONS_A)
  private radioGroupSpecialIntervetionsA!: RadioGroupAPI;

  @Inject(ScoreNasViewerAliases.RADIO_GROUP_SPECIAL_INTERVETIONS_B)
  private radioGroupSpecialIntervetionsB!: RadioGroupAPI;

  @OnMount({ target: ScoreNasViewerAliases.LAYOUT_SCORE_NAS_VIEWER })
  handleOnMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    InformationBarUtils.collapsedBar = true;
    this.context.set({encounterId, patientId});
    ScoreListContext.currentTab = ScoreType.SCORE_NAS
  }

  @OnMount({ target: ScoreNasViewerAliases.FORM_SCORE_NAS_VIEWER })
  handleOnMountForm(): void {
    this.formAPI.setLoading(true);
    this.fetchScoreNas()
  }

  private setValuesInRadioGroup(scoreNas: ScoreNASResponse): void {
    const radioGroupArray = this.getRadioGroupArray();

    radioGroupArray.forEach((radioGroup) => {
      radioGroup.field?.setData(radioExpressionData)
      Object.keys(scoreNas).map((key) => {
        if (key === radioGroup.props) {
          const value = scoreNas[key as keyof typeof scoreNas] === 'N' ? 0 : 1
          radioGroup.field?.setValue(radioExpressionData[value])
          this.formAPI.setData({...this.formAPI.getData(), [key]: radioExpressionData[value]})
        }
      })
    })
  }

  private fetchScoreNas(): Promise<void> {
    const { idScoreIndexes} = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && ScoreAndIndexesService.isAllowEdit;

    return HttpService.get(Endpoint.SCORE_NAS, { sequenceNumber: idScoreIndexes })
      .then(res => {
        const record = res.body as ScoreNASResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const dtEvaluation = DateFormatterUtil.timeStamp(String(record.dtEvaluation));
        const hour =  record.hour?.description;
        const showInactivate = record.professionalCode === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        this.formAPI.setEditing(true);
        this.formAPI.validate();
        this.formAPI.setDisabled(true);
        this.inputDateAPI.setValue(dtEvaluation);
        this.inputHourAPI.setValue(record.hour?.description as string);
        this.inputProfessional.setValue(record.professionalName as string);
        this.actionInactivateAPI.setEnabled(showInactivate);
        this.formAPI.setData({...record, dtEvaluation, time: hour});

        this.setValuesInRadioGroup(record)

      }).catch((err) => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formAPI.setLoading(false);
        this.actionNewAPI.setEnabled(allowInfo);
        this.actionReferenceAPI.setEnabled(allowInfo);
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-nas-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private getReference(): void {
    const scoreType = 'nas';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  private getRadioGroupArray(): RadioGroupData[] {
    return [{ field: this.radioGroupMonitoringControlA, props: RadioGroupExpressions.DS_ITEM_1A }, { field: this.radioGroupMonitoringControlB, props: RadioGroupExpressions.DS_ITEM_1B },
      { field: this.radioGroupMonitoringControlC, props: RadioGroupExpressions.DS_ITEM_1C }, { field: this.radioGroupLaboratory, props: RadioGroupExpressions.DS_ITEM_2 }, { field: this.radioGroupMedication, props: RadioGroupExpressions.DS_ITEM_3 },
      { field: this.radioGroupHygieneA, props: RadioGroupExpressions.DS_ITEM_4A }, { field: this.radioGroupHygieneB, props:RadioGroupExpressions.DS_ITEM_4B }, { field: this.radioGroupHygieneC, props: RadioGroupExpressions.DS_ITEM_4C },
      { field: this.radioGroupDrain, props: RadioGroupExpressions.DS_ITEM_5 }, { field: this.radioGroupMobilityA, props: RadioGroupExpressions.DS_ITEM_6A }, { field: this.radioGroupMobilityB, props: RadioGroupExpressions.DS_ITEM_6B },
      { field: this.radioGroupMobilityC, props: RadioGroupExpressions.DS_ITEM_6C }, { field: this.radioGroupSupportA, props: RadioGroupExpressions.DS_ITEM_7A }, { field: this.radioGroupSupportB, props: RadioGroupExpressions.DS_ITEM_7B },
      { field: this.radioGroupAdminTasksA, props: RadioGroupExpressions.DS_ITEM_8A}, { field: this.radioGroupAdminTasksB, props: RadioGroupExpressions.DS_ITEM_8B }, { field: this.radioGroupAdminTasksC, props: RadioGroupExpressions.DS_ITEM_8C },
      { field: this.radioGroupSupportVentilatoryA, props: RadioGroupExpressions.DS_ITEM_9 },{ field: this.radioGroupSupportVentilatoryB, props: RadioGroupExpressions.DS_ITEM_10 },
      { field: this.radioGroupSupportVentilatoryC, props: RadioGroupExpressions.DS_ITEM_11 }, { field: this.radioGroupSupportCardioA, props: RadioGroupExpressions.DS_ITEM_12 }, { field: this.radioGroupSupportCardioB, props: RadioGroupExpressions.DS_ITEM_13 },
      { field: this.radioGroupSupportCardioC, props: RadioGroupExpressions.DS_ITEM_14 }, { field: this.radioGroupSupportCardioD, props: RadioGroupExpressions.DS_ITEM_15 }, { field: this.radioGroupRenalSupportA, props: RadioGroupExpressions.DS_ITEM_16 },
      { field: this.radioGroupRenalSupportB, props: RadioGroupExpressions.DS_ITEM_17 }, { field: this.radioGroupNeurologicSupport, props: RadioGroupExpressions.DS_ITEM_18 },
      { field: this.radioGroupMetabolicSupportA, props: RadioGroupExpressions.DS_ITEM_19 }, { field: this.radioGroupMetabolicSupportB, props: RadioGroupExpressions.DS_ITEM_20 }, { field: this.radioGroupMetabolicSupportC, props: RadioGroupExpressions.DS_ITEM_21 },
      { field: this.radioGroupSpecialIntervetionsA, props: RadioGroupExpressions.DS_ITEM_22 }, { field: this.radioGroupSpecialIntervetionsB, props: RadioGroupExpressions.DS_ITEM_23 }];
  }

  @OnClick({ target: ScoreNasViewerAliases.ACTION_NEW_SCORE_NAS })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.SCORE_NAS_CREATE, {
      encounterId,
      patientId
    });
  }

  @OnClick({ target: ScoreNasViewerAliases.ACTION_INACTIVATE_SCORE_NAS })
  handleClickInactivate(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-inactivate',
      id: 'score-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idScoreIndexes,
    }

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: ScoreNasViewerAliases.ACTION_REFERENCE_SCORE_NAS })
  handleClickReference(): void {
    this.getReference();
  }

  @OnUnmount(ScoreNasViewerAliases.LAYOUT_SCORE_NAS_VIEWER)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_NAS;
  }
}
